import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  onSave,
  refinePayload,
  useNotification,
  fieldValidation,
  getDialogHeading,
  showBackdropLoader,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import DialogContent from '@mui/material/DialogContent';
import {
  putTutoringSubjectV2,
  postTutoringSubjectV2,
} from 'layouts/tutoringV2/services/tutoringAndSubjectsV2Api';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { PaperProps, characterLimit, tutorSubjectSuccess } from 'constants';

const subjectsAndTurorsSchema = yup.object().shape({
  subjectName: fieldValidation(characterLimit),
  maxConcurrentSessions: yup
    .number()
    .typeError('Capacity must be a number')
    .required('Capacity is required')
    .min(1, 'Minimum capacity is 1')
    .max(50, 'Maximum capacity is 50'),
});

const SubjectsAndTurorsV2Dialog = (props) => {
  const {
    open,
    setOpen,
    newSubject,
    setNewSubject,
    isFormLoading,
    fetchTutoringSubjects,
    tutoringSubjectV2Form,
    setTutoringSubjectV2Form,
  } = props;

  const { subjectId, subjectName, maxConcurrentSessions } =
    tutoringSubjectV2Form || {};

  const { setOpenNotification, setNotificationMessage, NotificationPopup } =
    useNotification();
  const dispatch = useDispatch();

  const [loadingButton, setLoadingButton] = useState(false);

  const defaultValue = {
    subjectName: subjectName || '',
    maxConcurrentSessions: maxConcurrentSessions || 1,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(subjectsAndTurorsSchema),
  });

  const { handleSubmit, reset, control, formState } = useFunction;
  const { errors } = formState;

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
    setTutoringSubjectV2Form({});
  };

  useEffect(() => {
    reset(defaultValue);
  }, [subjectName, maxConcurrentSessions]);

  const onSubmit = async (data) => {
    const { subjectName, maxConcurrentSessions } = data;

    const payload = {
      name: subjectName,
      maxConcurrentSessions: Number(maxConcurrentSessions),
    };

    onSave(
      tutorSubjectSuccess,
      newSubject,
      postTutoringSubjectV2,
      putTutoringSubjectV2,
      refinePayload(payload),
      subjectId,
      fetchTutoringSubjects,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton,
    );
    setNewSubject(false);
  };

  return (
    <>
      {showBackdropLoader(isFormLoading)}
      <Dialog open={open} PaperProps={PaperProps}>
        {getDialogHeading('New Subject', handleClose)}

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInputText
                type="text"
                required={true}
                control={control}
                name="subjectName"
                label="Subject Name"
                errors={!!errors.subjectName}
                helperText={errors?.subjectName?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                type="number"
                required
                control={control}
                name="maxConcurrentSessions"
                label="Max Concurrent Sessions"
                inputProps={{ min: 1, max: 50 }}
                errors={!!errors.maxConcurrentSessions}
                helperText={errors?.maxConcurrentSessions?.message}
              />
            </Grid>
            {renderSaveCancelButtons(
              handleClose,
              handleSubmit(onSubmit),
              loadingButton,
            )}
          </Grid>
        </DialogContent>
        <NotificationPopup />
      </Dialog>
    </>
  );
};

export default SubjectsAndTurorsV2Dialog;
